import { useState, useRef, useReducer } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, FormGroup, Typography } from '@mui/material';

import { ProfileInfo, profileInfoSchema, SubmitProfileData } from 'entities/profile';
// eslint-disable-next-line import/no-cycle
import {
  ReactHookFormBuilder as FormBuilder,
  Form,
  useYupValidationResolver,
} from 'shared/ui/form';
import users from 'app/users/users.resource';
import { PopoverWithArrow } from 'shared/ui/popover';
import { t, t_prefixed } from 'lib/i18n';
import { Button, Checkbox } from 'lib/ui';
import { ConfirmEmailButton } from 'features/update-profile-info';

import './ProfileInfoForm.scss';
import { TgPopoverContent } from './tg-popover-content/TgPopoverContent';

const { block, element } = bem('ProfileInfoForm');

interface ProfileInfoFormProps {
  initValues: ProfileInfo;
  onSubmit: (formData: SubmitProfileData) => void;
  email: string;
  isEmailConfirmed: boolean;
}

const label = t_prefixed('profile_form');

const notificationOptions = [
  {
    value: 'push',
    label: label('notification_types.push'),
  },
  {
    value: 'sms',
    label: label('notification_types.sms'),
  },
  {
    value: 'email',
    label: label('notification_types.email'),
  },
];

const notificationTelegram = {
  value: 'telegram',
  label: label('notification_types.telegram'),
};

const CheckBoxGroup = () => {
  const { control } = useFormContext();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const tgCheckboxRef = useRef<HTMLDivElement>(null);

  const toggleTgvalue = (field) => {
    field.onChange(handleSelect(notificationTelegram.value, field.value));
  };

  const handleClose = (field) => {
    setAnchorEl(null);
    users.current.invalidate();
    toggleTgvalue(field);
    forceUpdate();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleSelect = (value: string, fieldValue: string[] = []) => {
    return fieldValue.includes(value)
      ? fieldValue.filter((i) => i !== value)
      : [...fieldValue, value];
  };

  const checkTgCheckbox = (field) => {
    toggleTgvalue(field);
    if (!field.value.includes(notificationTelegram.value) && tgCheckboxRef.current) {
      setAnchorEl(tgCheckboxRef.current);
      if (
        tgCheckboxRef.current.getBoundingClientRect().top < 95 ||
        tgCheckboxRef.current.getBoundingClientRect().bottom > window.innerHeight - 95
      ) {
        tgCheckboxRef.current.scrollIntoView({ block: 'center' });
      }
    }
  };

  return (
    <Controller
      name="notifications"
      control={control}
      render={({ field }) => (
        <FormControl {...element('checkboxController')}>
          <Typography {...element('checkboxLabel')}>{label('notifications')}</Typography>
          <FormGroup {...element('checkboxGroup')}>
            {notificationOptions.map((item) => {
              return (
                <Checkbox
                  key={item.label}
                  label={item.label}
                  checked={!!field.value?.includes(item.value)}
                  onChange={() => field.onChange(handleSelect(item.value, field.value))}
                />
              );
            })}
            <div aria-describedby={id} ref={tgCheckboxRef}>
              <Checkbox
                {...element('tgCheckbox')}
                key={notificationTelegram.label}
                label={notificationTelegram.label}
                checked={!!field.value?.includes(notificationTelegram.value)}
                onChange={() => checkTgCheckbox(field)}
              />
            </div>
          </FormGroup>
          <PopoverWithArrow
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={() => handleClose(field)}
          >
            <TgPopoverContent />
          </PopoverWithArrow>
        </FormControl>
      )}
    />
  );
};

const SubmitButton = () => {
  const {
    formState: { isDirty, isSubmitting },
  } = useFormContext();

  return (
    <Button type="submit" disabled={!isDirty || isSubmitting}>
      {t('common.save')}
    </Button>
  );
};

export const ProfileInfoForm = ({
  initValues,
  onSubmit,
  email,
  isEmailConfirmed,
}: ProfileInfoFormProps) => {
  const { resolver } = useYupValidationResolver(profileInfoSchema);

  return (
    <div {...block()}>
      <Form initValues={initValues} resolver={resolver} onSubmit={onSubmit}>
        <FormBuilder.Text required title={label('surname')} name="surname" />
        <FormBuilder.Text required title={label('name')} name="name" />
        <FormBuilder.Text title={label('patronymic')} name="patronymic" />
        <FormBuilder.Tel title={label('mobile')} name="mobile" />
        <ConfirmEmailButton savedEmail={email} isEmailConfirmed={isEmailConfirmed} />
        <CheckBoxGroup />
        <FormBuilder.Buttons left>
          <SubmitButton />
        </FormBuilder.Buttons>
      </Form>
    </div>
  );
};
